import { useSortable } from "@dnd-kit/sortable";
import React, { useEffect, useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import { APIFieldType, FieldType } from "../../../services/helpers/types";
import Button from "../Button/Button";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { DeleteOutline, Edit } from "@mui/icons-material";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { fieldRenderer } from "../../molecules/Field/helper";
import Drawer from "../../molecules/Drawer/Drawer";
import AddFieldValuesForm from "../../organisms/AddFieldValuesForm/AddFieldValuesForm";
import { getFieldClassName } from "../../organisms/Fields/helpers";
import { useDispatch } from "react-redux";
import useEditDrawerState from "../../../services/hooks/useEditFieldState";
import { setIsEditDrawerOpen } from "../../../redux/reducers/appReducer";
import { useTranslation } from "react-i18next";

interface DraggableFieldsItemProps {
  id: number;
  field: FieldType | undefined;
  onEditField: (itemID: number, newField: APIFieldType) => void;
  onDelete: (itemID: number) => void;
  onCreateField: (fieldID: number, newField: APIFieldType) => void;
}

const DraggableFieldsItem = (props: DraggableFieldsItemProps) => {
  const { id, field, onEditField, onDelete, onCreateField } = props;

  const isTabletView = window.innerWidth < 1440;

  const [fieldValue, setFieldValue] = useState<string | undefined>(
    field?.defaultValue
  );

  useEffect(() => {
    setFieldValue(field?.defaultValue);
  }, [field?.defaultValue]);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const dispatch = useDispatch();
  const editDrawerState = useEditDrawerState();
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleConfirmDelete = () => {
    if (field?.fieldID !== undefined) {
      onDelete(parseInt(field?.fieldID.toString()));
    }
  };

  const handleFieldChange = (value?: string) => {
    setFieldValue(value);
  };

  const fieldClassName = getFieldClassName(field?.type, false);

  if (field !== undefined) {
    return (
      <>
        <div ref={setNodeRef} style={style} className={"dui-fields-edit-item"}>
          <div className="dui-fields-edit-item-buttons">
            <div {...attributes} {...listeners} className="dui-btn-inner">
              <Button
                icon={<UnfoldMoreIcon />}
                type="text"
                className="dui-sort-btn"
              />
            </div>
            <div className="dui-btn-inner">
              <Button
                icon={<Edit />}
                type="text"
                onClick={() => {
                  dispatch(
                    setIsEditDrawerOpen({
                      isOpen: true,
                      type: `edit${field.fieldID}`,
                    })
                  );
                }}
              />
            </div>
            <div className="dui-btn-inner">
              <Button
                icon={<DeleteOutline />}
                type="text"
                onClick={() => {
                  setIsDeleteOpen(true);
                }}
              />
            </div>
          </div>
          <div className={`dui-fields-edit-item-name ${fieldClassName}`}>
            <div className="dui-fields-edit-item-title">{field.name}</div>
            <div className="dui-fields-edit-item-field">
              {fieldRenderer(field, handleFieldChange, fieldValue)}
            </div>
          </div>
          <ConfirmDialog
            open={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={handleConfirmDelete}
            title={t("deleteTitle")}
            description={`${t("areYouSureDelete1")} ${field.name} ${t("areYouSureDelete2")}`}
            confirmText={t("delete")}
            cancelText={t("cancel")}
            confirmButtonAppearance="error"
          />
        </div>
        <Drawer
          isOpen={
            editDrawerState.isOpen &&
            editDrawerState.type === `edit${field.fieldID}`
          }
          onClose={() =>
            dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }))
          }
          widthRefElID={isTabletView ? "dui-main-page" : "dui-doctors-report"}
        >
          <AddFieldValuesForm
            field={field}
            type={field.type}
            tabID={field.tabID}
            position={field.position}
            onBack={() =>
              dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }))
            }
            mode="edit"
            afterCreate={onCreateField}
            onEditField={onEditField}
          />
        </Drawer>
      </>
    );
  }
  return <></>;
};

export default DraggableFieldsItem;
